import { Plus } from "lucide-react-native";
import { FC, useState } from "react";
import { Platform, Pressable } from "react-native";

import { Box, HStack } from "@/components/ui";

interface AddQuestionProps {
  /** When true, no hover affordance and no click — kept rendered so the
      layout slot stays consistent across interactive and read-only
      modes. */
  disabled?: boolean;
  onPress: () => void;
}

/**
 * Inline "insert here" affordance rendered between questions, above
 * each section header, after each section's last question, and below
 * the last group. The slot itself is a flat 12px tall (`paddingVertical:
 * 6`) — same vertical rhythm whether idle or hovered, no layout
 * shift. The divider + plus are absolutely positioned and centered
 * over that slot, faded in on hover via `opacity`. The plus icon
 * overflows the 12px slot but that's visually fine because adjacent
 * cards already pad away from the gap. Disabled mode keeps the
 * affordance invisible (no action) but the wrapper still reserves
 * the slot for read-only / locked layouts.
 */
const AddQuestion: FC<AddQuestionProps> = ({ disabled = false, onPress }) => {
  const [hovered, setHovered] = useState(false);
  const isWeb = Platform.OS === "web";
  const active = !disabled && hovered;

  return (
    <Pressable
      accessibilityRole="button"
      disabled={disabled}
      onHoverIn={isWeb ? () => setHovered(true) : undefined}
      onHoverOut={isWeb ? () => setHovered(false) : undefined}
      onPress={disabled ? undefined : onPress}
      style={{
        cursor: disabled ? "auto" : "pointer",
        height: 20,
        justifyContent: "center",
        width: "100%",
      }}
    >
      <HStack
        className="items-center gap-2"
        style={{
          left: 0,
          opacity: active ? 1 : 0,
          pointerEvents: "none",
          position: "absolute",
          right: 0,
        }}
      >
        <Box className="h-px flex-1 bg-primary-400" />
        <Plus color="rgb(var(--color-primary-500))" size={16} />
        <Box className="h-px flex-1 bg-primary-400" />
      </HStack>
    </Pressable>
  );
};

export default AddQuestion;
